import { useState, useEffect, useCallback } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import { Icon } from '../components/SvgIcons';
import './SuperAdminSettings.css';

/* ─────────────────────────────────────────
   MODEL LIST EDITOR (add / delete / reorder)
───────────────────────────────────────── */
const ModelList = ({ models, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v || models.includes(v)) return;
    onChange([...models, v]);
    setDraft('');
  };
  const remove = (idx) => onChange(models.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...models];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    onChange(next);
  };

  return (
    <div>
      {models.length === 0 && (
        <p className="sas-ml-empty">No models configured — add one below.</p>
      )}
      {models.map((m, i) => (
        <div key={m} className="sas-ml-row">
          <span className="sas-ml-name">{m}</span>
          <span className="sas-ml-pri">#{i + 1}</span>
          <button type="button" className="sas-ml-iconbtn" onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</button>
          <button type="button" className="sas-ml-iconbtn" onClick={() => move(i, 1)} disabled={i === models.length - 1} title="Move down">↓</button>
          <button type="button" className="sas-ml-iconbtn sas-ml-del" onClick={() => remove(i)} title="Remove">×</button>
        </div>
      ))}
      <div className="sas-ml-add">
        <input
          className="sas-input"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <button type="button" className="sas-btn sas-btn-primary" onClick={add}>Add</button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   AI SETTINGS TAB
───────────────────────────────────────── */
const AISettingsTab = () => {
  const { showToast } = useToast();

  const [, setGeminiKey]                  = useState('');

  const [aiProvider, setAiProvider]       = useState('ollama');
  const [aiStatus, setAiStatus]           = useState(null);
  const [switchingProvider, setSwitching] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  const [geminiModels, setGeminiModels]             = useState([]);
  const [geminiVisionModels, setGeminiVisionModels] = useState([]);
  const [modelsSaving, setModelsSaving]             = useState(false);

  const fetchSettings = useCallback(async () => {
    setStatusLoading(true);
    try {
      const [keyR, provR, statusR] = await Promise.all([
        API.get('/superadmin/ai-settings').catch(() => ({ data: {} })),
        API.get('/ai-settings/provider').catch(() => ({ data: { provider: 'ollama' } })),
        API.get('/ai-settings/status').catch(() => ({ data: {} })),
      ]);
      if (keyR.data.geminiApiKey) setGeminiKey(keyR.data.geminiApiKey);
      const s = keyR.data.settings || {};
      setGeminiModels(Array.isArray(s.geminiModels) ? s.geminiModels : []);
      setGeminiVisionModels(Array.isArray(s.geminiVisionModels) ? s.geminiVisionModels : []);
      setAiProvider(provR.data.provider || 'ollama');
      setAiStatus(statusR.data.ai_status || null);
    } catch {
      // silently fail if AI endpoints not configured
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveModels = async () => {
    setModelsSaving(true);
    try {
      await API.put('/superadmin/ai-settings', { geminiModels, geminiVisionModels });
      showToast('Gemini models saved.', 'success');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save models', 'error');
    } finally {
      setModelsSaving(false);
    }
  };

  const switchProvider = async (p) => {
    if (p === aiProvider || switchingProvider) return;
    setSwitching(true);
    try {
      await API.put('/ai-settings/provider', { provider: p });
      setAiProvider(p);
      showToast(`Switched to ${p === 'ollama' ? 'Ollama' : 'Gemini'}`, 'success');
      await fetchSettings();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to switch provider', 'error');
    } finally {
      setSwitching(false);
    }
  };

  const resetCircuitBreaker = async () => {
    try {
      await API.post('/ai-settings/reset-circuit-breaker');
      showToast('Circuit breaker reset', 'success');
      fetchSettings();
    } catch {
      showToast('Failed to reset circuit breaker', 'error');
    }
  };

  const providers = [
    {
      id: 'ollama',
      icon: 'Home',
      name: 'Ollama',
      desc: 'Local inference — private, no API costs',
      models: 'edulab/edulab1:latest · llama3.1:8b · qwen2.5vl:7b',
    },
    {
      id: 'gemini',
      icon: 'Sparkles',
      name: 'Gemini',
      desc: 'Google Cloud AI — powerful, requires API key',
      models: 'gemini-2.0-flash · gemini-1.5-pro',
    },
  ];

  return (
    <div className="sas-tab-content">

      {/* Provider Toggle */}
      <div className="sas-card">
        <div className="sas-card-header">
          <div>
            <h3 className="sas-card-title">AI Model Provider</h3>
            <p className="sas-card-sub">Switch between local Ollama and Google Gemini</p>
          </div>
          {aiProvider && (
            <span className={`sas-provider-badge sas-provider-badge--${aiProvider}`}>
              {aiProvider === 'ollama' ? 'Ollama' : 'Gemini'} Active
            </span>
          )}
        </div>
        <div className="sas-provider-list">
          {providers.map(({ id, icon, name, desc, models }) => (
            <div
              key={id}
              className={`sas-provider-option${aiProvider === id ? ' sas-provider-option--active' : ''}${switchingProvider ? ' sas-provider-option--disabled' : ''}`}
              onClick={() => switchProvider(id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && switchProvider(id)}
            >
              <div className="sas-provider-icon"><Icon name={icon} size={26} /></div>
              <div className="sas-provider-body">
                <div className="sas-provider-name">{name}</div>
                <div className="sas-provider-desc">{desc}</div>
                <div className="sas-provider-models">{models}</div>
              </div>
              {aiProvider === id && <div className="sas-provider-check">Active</div>}
            </div>
          ))}
        </div>
        {switchingProvider && <p className="sas-switching">Switching provider...</p>}
      </div>

      {aiProvider === 'gemini' && (
        <div className="sas-card">
          <div className="sas-card-header">
            <div>
              <h3 className="sas-card-title" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}><Icon name="Sparkles" size={16} /> Gemini Models</h3>
              <p className="sas-card-sub">Ordered rotation — model #1 is tried first, then falls back down the list. Latest Gemini 3 models on top.</p>
            </div>
            <button className="sas-btn sas-btn-primary sas-btn-sm" onClick={saveModels} disabled={modelsSaving}>
              {modelsSaving ? 'Saving…' : 'Save Models'}
            </button>
          </div>

          <div className="sas-form-group">
            <label className="sas-label">Generation Models</label>
            <ModelList
              models={geminiModels}
              onChange={setGeminiModels}
              placeholder="e.g. gemini-3-pro-preview"
            />
            <span className="sas-hint">Used for question / text generation.</span>
          </div>

          <div className="sas-form-group">
            <label className="sas-label">Vision / Extraction Models</label>
            <ModelList
              models={geminiVisionModels}
              onChange={setGeminiVisionModels}
              placeholder="e.g. gemini-3-pro-preview"
            />
            <span className="sas-hint">Used for PDF template extraction and image analysis.</span>
          </div>
        </div>
      )}

      {/* Gemini API Key */}
      {/* <div className="sas-card">
        <div className="sas-card-header">
          <div>
            <h3 className="sas-card-title">Gemini API Key</h3>
            <p className="sas-card-sub">Required when using Gemini as the AI provider</p>
          </div>
        </div>
        <form onSubmit={saveKey}>
          <div className="sas-form-group">
            <label className="sas-label">API Key</label>
            <input
              type="password"
              className="sas-input"
              value={geminiKey}
              onChange={e => setGeminiKey(e.target.value)}
              placeholder="Enter Gemini API key"
            />
            <span className="sas-hint">
              Leave empty to use the key from environment variables.
            </span>
          </div>
          <button type="submit" className="sas-btn sas-btn-primary" disabled={keySaving}>
            {keySaving ? 'Saving...' : 'Save API Key'}
          </button>
        </form>
      </div> */}

      {/* Status Cards */}
      {!statusLoading && aiStatus && (
        <div className="sas-status-grid">
          <div className="sas-card">
            <div className="sas-card-header">
              <h3 className="sas-card-title">Circuit Breaker</h3>
              <button className="sas-btn sas-btn-ghost sas-btn-sm" onClick={resetCircuitBreaker}>
                Reset
              </button>
            </div>
            {[
              ['State',     <span key="state" className={`sas-cb-state sas-cb-state--${aiStatus.circuit_breaker?.state || 'closed'}`}>{aiStatus.circuit_breaker?.state || 'closed'}</span>],
              ['Failures',  aiStatus.circuit_breaker?.failureCount ?? 0],
              ['Threshold', aiStatus.circuit_breaker?.threshold ?? 5],
            ].map(([k, v]) => (
              <div key={k} className="sas-stat-row">
                <span className="sas-stat-key">{k}</span>
                <span className="sas-stat-val">{v}</span>
              </div>
            ))}
          </div>

          <div className="sas-card">
            <div className="sas-card-header">
              <h3 className="sas-card-title">Rate Limiter</h3>
            </div>
            {[
              ['Tokens / min',   aiStatus.rate_limiter?.tokensUsedThisMinute ?? 0],
              ['Requests / min', aiStatus.rate_limiter?.requestsThisMinute ?? 0],
              ['Active Model',   aiStatus.model_selector?.currentModel || '—'],
            ].map(([k, v]) => (
              <div key={k} className="sas-stat-row">
                <span className="sas-stat-key">{k}</span>
                <span className="sas-stat-val">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

/* ─────────────────────────────────────────
   2FA SECURITY TAB
───────────────────────────────────────── */
const TwoFASecurityTab = () => {
  const [qrCode, setQrCode]   = useState('');
  const [secret, setSecret]   = useState('');
  const [token, setToken]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [isEnabled, setEnabled] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => { checkStatus(); }, []);

  const checkStatus = async () => {
    try {
      const { data } = await API.get('/auth/2fa/status');
      setEnabled(data.enabled);
    } catch {}
  };

  const handleSetup = async () => {
    try {
      setLoading(true); setError('');
      const { data } = await API.post('/auth/2fa/setup');
      setQrCode(data.qrCode); setSecret(data.secret);
    } catch (err) { setError(err.response?.data?.message || 'Failed to setup 2FA'); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      setLoading(true); setError('');
      await API.post('/auth/2fa/verify', { token });
      setSuccess('2FA enabled successfully!');
    } catch (err) { setError(err.response?.data?.message || 'Invalid token'); }
    finally { setLoading(false); }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to disable 2FA?')) return;
    try {
      setLoading(true); setError('');
      await API.post('/auth/2fa/disable', { password });
      setSuccess('2FA disabled successfully!');
      setEnabled(false); setPassword(''); setQrCode(''); setSecret('');
    } catch (err) { setError(err.response?.data?.message || 'Failed to disable 2FA'); }
    finally { setLoading(false); }
  };

  return (
    <div className="sas-tab-content">
      <div className="sas-card" style={{ maxWidth: 560 }}>
        {error   && <div className="sas-alert sas-alert--error">{error}</div>}
        {success && <div className="sas-alert sas-alert--success">{success}</div>}

        {isEnabled ? (
          <div>
            <div className="sas-2fa-hero">
              <div className="sas-2fa-icon"><Icon name="CheckCircle" size={48} color="var(--success)" /></div>
              <h3>2FA is Enabled</h3>
              <p>Your account is protected with two-factor authentication.</p>
            </div>
            <form onSubmit={handleDisable}>
              <div className="sas-form-group">
                <label className="sas-label">Enter your password to disable 2FA</label>
                <input
                  type="password" className="sas-input"
                  value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password" required
                />
              </div>
              <button type="submit" className="sas-btn sas-btn-danger" disabled={loading} style={{ width: '100%' }}>
                {loading ? 'Disabling...' : 'Disable 2FA'}
              </button>
            </form>
          </div>
        ) : !qrCode ? (
          <div className="sas-2fa-setup">
            <div className="sas-2fa-hero">
              <div className="sas-2fa-icon"><Icon name="Lock" size={48} /></div>
              <h3>Enable Two-Factor Authentication</h3>
              <p>Add an extra layer of security to your account.</p>
            </div>
            <div className="sas-info-box" style={{ marginBottom: '1.5rem' }}>
              <strong>How to set up:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                <li>Download Google Authenticator or any TOTP app</li>
                <li>Click "Setup 2FA" to generate a QR code</li>
                <li>Scan the QR code with your authenticator app</li>
                <li>Enter the 6-digit code to verify</li>
              </ul>
            </div>
            <button onClick={handleSetup} className="sas-btn sas-btn-primary" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Setting up...' : 'Setup 2FA'}
            </button>
          </div>
        ) : (
          <div>
            <h3 style={{ textAlign: 'center', marginBottom: '1.5rem', color: 'var(--text-primary)' }}>Scan QR Code</h3>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ background: 'white', padding: '1rem', borderRadius: '12px', display: 'inline-block', boxShadow: 'var(--shadow-md)' }}>
                <img src={qrCode} alt="QR Code" style={{ width: 220, height: 220 }} />
              </div>
            </div>
            <div className="sas-info-box" style={{ marginBottom: '1.25rem' }}>
              <strong>Manual Entry Key:</strong>
              <p style={{ marginTop: '0.4rem', wordBreak: 'break-all', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{secret}</p>
            </div>
            <form onSubmit={handleVerify}>
              <div className="sas-form-group">
                <label className="sas-label">6-digit code from your authenticator app</label>
                <input
                  type="text" className="sas-input"
                  value={token}
                  onChange={e => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" maxLength="6" required
                  style={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                />
              </div>
              <button type="submit" className="sas-btn sas-btn-primary" disabled={loading || token.length !== 6} style={{ width: '100%' }}>
                {loading ? 'Verifying...' : 'Verify and Enable'}
              </button>
            </form>
            <button
              onClick={() => { setQrCode(''); setSecret(''); setToken(''); }}
              className="sas-btn sas-btn-ghost"
              style={{ width: '100%', marginTop: '0.75rem' }}
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
const SuperAdminSettings = () => {
  const [activeTab, setActiveTab] = useState('ai');

  const tabs = [
    { id: 'ai',  label: 'AI Settings',   icon: 'Sparkles' },
    { id: '2fa', label: '2FA Security',  icon: 'Lock' },
  ];

  return (
    <div className="sas-root">
      <div className="sas-page-header">
        <h1 className="sas-page-title">Settings</h1>
        <p className="sas-page-sub">Manage AI configuration and account security</p>
      </div>

      <div className="sas-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={`sas-tab${activeTab === t.id ? ' sas-tab--active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            <Icon name={t.icon} size={15} /> {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'ai'  && <AISettingsTab />}
      {activeTab === '2fa' && <TwoFASecurityTab />}
    </div>
  );
};

export default SuperAdminSettings;
